import * as Phaser from "phaser"
import { EventBus } from "../core/event-bus"
import { InputManager } from "../core/input-mapper"
import { EVENT, SCENE } from "../core/keys"
import { THEME } from "../core/theme"
import { playConfirm, playHover } from "../core/ui-sfx"
import { GAME_HEIGHT, GAME_WIDTH } from "../data/constants"
import { PortalManager } from "../systems/portal-manager"
import { RetroButton } from "../ui/components"
import {
  NEON,
  applyModalCameraFx,
  clearCameraFx,
  drawNeonLine,
  drawScanlines,
  neonTextShadow,
} from "../ui/neon-fx"
import { getWaveRuntime } from "./wave-runtime-api"

const FONT = THEME.fonts
const COLOR = THEME.colors

export class PauseScene extends Phaser.Scene {
  private waveCamera?: Phaser.Cameras.Scene2D.Camera
  private inputManager!: InputManager
  private selectedIndex = 0
  private pauseButtons: RetroButton[] = []

  constructor() {
    super({ key: SCENE.PAUSE })
  }

  create(): void {
    this.selectedIndex = 0
    this.pauseButtons = []

    // Pause the wave scene
    if (this.scene.isActive(SCENE.WAVE)) {
      this.scene.pause(SCENE.WAVE)
    }

    if (this.scene.isPaused(SCENE.WAVE) || this.scene.isActive(SCENE.WAVE)) {
      this.waveCamera = this.scene.get(SCENE.WAVE).cameras.main
      applyModalCameraFx(this.waveCamera, {
        blurStrength: 1.5,
        blurSteps: 5,
        vignetteRadius: 0.54,
        vignetteStrength: 0.6,
      })
    }

    this.inputManager = new InputManager(this, {
      onDown: (action, event) => {
        // Ignore global pointer events for these actions in the menu
        if (event?.pointer && (action === "CONFIRM" || action === "FLAP"))
          return

        switch (action) {
          case "UP":
            this.moveSelection(-1)
            break
          case "DOWN":
            this.moveSelection(1)
            break
          case "CONFIRM":
          case "FLAP":
            this.confirmSelection()
            break
          case "BACK":
          case "PAUSE":
            this.handleResume()
            break
        }
      },
    })

    // Semi-transparent dark overlay
    this.add.rectangle(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      GAME_WIDTH,
      GAME_HEIGHT,
      COLOR.black,
      0.85,
    )

    const cx = GAME_WIDTH / 2
    const cy = GAME_HEIGHT / 2

    // Scanline overlay
    drawScanlines(this, 0, 0, GAME_WIDTH, GAME_HEIGHT, 0.03, 3).setDepth(10)

    // Neon accent lines
    const accentLines = this.add.graphics()
    drawNeonLine(
      accentLines,
      cx - 140,
      cy - 90,
      cx + 140,
      NEON.cyan,
      0.3,
      0.08,
      1,
      4,
    )
    drawNeonLine(
      accentLines,
      cx - 100,
      cy + 120,
      cx + 100,
      NEON.magenta,
      0.2,
      0.06,
      1,
      4,
    )

    // PAUSED title with neon glow
    this.add
      .text(cx, cy - 60, "PAUSED", {
        fontSize: "48px",
        fontFamily: FONT.sans,
        fontStyle: "900",
        color: COLOR.warningText,
        shadow: neonTextShadow(THEME.shadows.gold, 16),
      })
      .setOrigin(0.5)

    // Subtitle
    this.add
      .text(cx, cy - 20, "Even good boys need a break!", {
        fontSize: "13px",
        fontFamily: FONT.sans,
        fontStyle: "500",
        color: COLOR.cyanText,
        shadow: neonTextShadow(THEME.shadows.cyan, 4),
      })
      .setOrigin(0.5)
      .setAlpha(0.5)

    // ── RESUME button ───────────────────────────────────────────
    const resumeBtn = new RetroButton(this, {
      inputManager: this.inputManager,
      x: cx - 110,
      y: cy + 10,
      width: 220,
      height: 48,
      text: "RESUME",
      color: 0x6f5618,
      fontSize: "8px",
      onClick: () => {
        this.handleResume()
      },
    })
    this.pauseButtons.push(resumeBtn)

    // ── QUIT button ─────────────────────────────────────────────
    const quitBtn = new RetroButton(this, {
      inputManager: this.inputManager,
      x: cx - 110,
      y: cy + 70,
      width: 220,
      height: 48,
      text: "QUIT TO MENU",
      color: 0x5f2a5d,
      fontSize: "8px",
      onClick: () => {
        this.handleQuit()
      },
    })
    this.pauseButtons.push(quitBtn)

    // ── OPTIONS button ──────────────────────────────────────────
    const optionsBtn = new RetroButton(this, {
      inputManager: this.inputManager,
      x: cx - 70,
      y: cy + 130,
      width: 140,
      height: 32,
      text: "OPTIONS",
      color: 0x2d4578,
      fontSize: "8px",
      onClick: () => {
        this.scene.stop(SCENE.PAUSE)
        this.scene.launch(SCENE.OPTIONS, { returnTo: SCENE.PAUSE })
      },
    })
    this.pauseButtons.push(optionsBtn)

    // ── PORTAL button ─────────────────────────────────────────
    if (window.location.search.includes("portal=true")) {
      const portalBtn = new RetroButton(this, {
        inputManager: this.inputManager,
        x: cx + 80,
        y: cy + 130,
        width: 100,
        height: 32,
        text: "PORTAL",
        color: 0x1a5a4a,
        fontSize: "8px",
        onClick: () => this.openPortal(),
      })
      this.pauseButtons.push(portalBtn)
    }

    this.updateSelection()

    // Cleanup
    this.events.once("shutdown", () => {
      this.inputManager.destroy()
      if (this.waveCamera) {
        clearCameraFx(this.waveCamera)
        this.waveCamera = undefined
      }
    })
  }

  private moveSelection(dir: number): void {
    playHover(this)
    this.selectedIndex =
      (this.selectedIndex + dir + this.pauseButtons.length) %
      this.pauseButtons.length
    this.updateSelection()
  }

  private confirmSelection(): void {
    if (this.selectedIndex === 0) {
      this.handleResume()
    } else if (this.selectedIndex === 1) {
      this.handleQuit()
    } else if (this.selectedIndex === 2) {
      this.scene.stop(SCENE.PAUSE)
      this.scene.launch(SCENE.OPTIONS, { returnTo: SCENE.PAUSE })
    }
  }

  private updateSelection(): void {
    for (let i = 0; i < this.pauseButtons.length; i++) {
      this.pauseButtons[i].setSelected(i === this.selectedIndex)
    }
  }

  private handleResume(): void {
    // Resume wave scene
    if (this.scene.isPaused(SCENE.WAVE)) {
      this.scene.resume(SCENE.WAVE)
    }

    // Tell WaveScene to resume internal state
    const waveRuntime = getWaveRuntime(this)
    if (waveRuntime) {
      waveRuntime.resumeGame()
    }

    EventBus.emit(EVENT.GAME_RESUMED)
    this.scene.stop(SCENE.PAUSE)
  }

  private handleQuit(): void {
    // Stop all gameplay scenes
    if (this.scene.isActive(SCENE.WAVE) || this.scene.isPaused(SCENE.WAVE)) {
      this.scene.stop(SCENE.WAVE)
    }
    if (this.scene.isActive(SCENE.HUD)) {
      this.scene.stop(SCENE.HUD)
    }

    this.scene.stop(SCENE.PAUSE)
    this.scene.start(SCENE.MENU)
  }

  private openPortal(): void {
    playConfirm(this)
    const url = PortalManager.instance.getExitPortalUrl()
    if (url) {
      window.location.href = url
    }
  }
}
